/**
 * Disk Manager Page Logic
 *
 * Manages USB disks and storage
 */

import { api } from '../api/client.js';
import { apiCall } from '../api/helpers.js';
import { initAuthenticatedPage, hasPermission } from '../app/init.js';
import { declareLoading, declareLoaded, notificationDialog } from '../ui/dialogs.js';
import { escapeHtml } from '../utils.js';

// Module state
let disksTable: unknown = null;
let refreshIntervalId: ReturnType<typeof setInterval> | null = null;

/**
 * Fill the disks table
 */
export async function fillDisksTable(): Promise<void> {
  const disks = await apiCall(api.listDisks(), 'Failed to Load Disks');
  if (!disks) {
    declareLoaded();
    return;
  }

  const tableData = disks.map((disk) => {
    return {
      name: escapeHtml(disk.name || 'Unknown'),
      path: escapeHtml(disk.path),
      size: disk.size_human || 'N/A',
      format: escapeHtml(disk.format || 'N/A'),
      status: disk.is_base ? 'Base Image' : disk.in_use ? 'In Use' : 'Available',
    };
  });

  if (!disksTable) {
    disksTable = $('#disks').DataTable({
      data: tableData,
      columns: [
        { data: 'name', title: 'Name' },
        { data: 'path', title: 'Path' },
        { data: 'size', title: 'Size' },
        { data: 'format', title: 'Format' },
        { data: 'status', title: 'Status' },
      ],
      paging: false,
      info: false,
      searching: false,
    });
    declareLoaded();
  } else {
    const dt = disksTable as { clear: () => unknown; rows: { add: (data: unknown) => unknown }; draw: () => void };
    dt.clear();
    dt.rows.add(tableData);
    dt.draw();
  }
}

/**
 * Initialize the disk manager page
 */
export async function initDiskmanPage(): Promise<void> {
  const user = await initAuthenticatedPage();
  if (!user) return; // Will redirect to login

  // Check permission
  if (!hasPermission('manage-disks')) {
    notificationDialog('Access denied: Disk management permission required');
    window.location.href = '/';
    return;
  }

  // Load disks table
  declareLoading();
  await fillDisksTable();

  // Show content
  const content = document.getElementById('content');
  if (content) content.style.display = 'block';

  // Set up periodic refresh
  refreshIntervalId = setInterval(fillDisksTable, 2000);
}

/**
 * Clean up when leaving the page
 */
export function cleanupDiskmanPage(): void {
  if (refreshIntervalId) {
    clearInterval(refreshIntervalId);
    refreshIntervalId = null;
  }
}

// Export to window
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).fillDisksTable = fillDisksTable;
  (window as unknown as Record<string, unknown>).initDiskmanPage = initDiskmanPage;
  (window as unknown as Record<string, unknown>).cleanupDiskmanPage = cleanupDiskmanPage;

  // Clean up on page unload
  window.addEventListener('beforeunload', cleanupDiskmanPage);
}
